"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/providers/auth-provider";
import { realtimeClient } from "@/realtime/realtime-client";
import type { RealtimeEvent } from "@/realtime/realtime-client";

interface RealtimeContextType {
  isConnected: boolean;
  lastEvent: RealtimeEvent | null;
  joinChannel: (channel: string) => () => void;
  leaveChannel: (channel: string) => void;
  publish: (channel: string, event: RealtimeEvent) => void;
}

const RealtimeContext = createContext<RealtimeContextType | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const channelsRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    if (!user) return;

    realtimeClient.onConnected = () => setIsConnected(true);
    realtimeClient.onDisconnected = () => setIsConnected(false);
    realtimeClient.onEvent = (event: RealtimeEvent) => {
      setLastEvent(event);
    };

    realtimeClient.connect();

    return () => {
      channelsRef.current.forEach((unsubscribe) => unsubscribe());
      channelsRef.current.clear();
      realtimeClient.disconnect();
    };
  }, [user]);

  const joinChannel = useCallback(
    (channel: string) => {
      const unsubscribe = realtimeClient.subscribe(channel, (event: RealtimeEvent) => {
        setLastEvent(event);
      });
      channelsRef.current.set(channel, unsubscribe);
      return unsubscribe;
    },
    []
  );

  const leaveChannel = useCallback(
    (channel: string) => {
      const unsubscribe = channelsRef.current.get(channel);
      if (unsubscribe) {
        unsubscribe();
        channelsRef.current.delete(channel);
      }
    },
    []
  );

  const publish = useCallback(
    (channel: string, event: RealtimeEvent) => {
      realtimeClient.publish(channel, event);
    },
    []
  );

  return (
    <RealtimeContext.Provider value={{ isConnected, lastEvent, joinChannel, leaveChannel, publish }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime(): RealtimeContextType {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error("useRealtime must be used within a RealtimeProvider");
  }
  return context;
}
